import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import muiThemeable from 'material-ui/styles/muiThemeable';
import { emphasize } from 'material-ui/utils/colorManipulator';
import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';
import Chip from 'material-ui/Chip';
import DateIcon from 'material-ui/svg-icons/action/update';
import Avatar from 'material-ui/Avatar';
import TextField from 'material-ui/TextField';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import Divider from 'material-ui/Divider';
import Paper from 'material-ui/Paper';
import Dialog from 'material-ui/Dialog';
import RefreshIndicator from 'material-ui/RefreshIndicator';
// App
import './StepIdentity.css';
import API from './api';
import UnknownCustomerDialog from './components/UnknownCustomerDialog';

moment.locale('fr');
let phoneUtil = PhoneNumberUtil.getInstance();

class StepIdentity extends Component {

    static propTypes = {
        allowNewCustomer: PropTypes.bool,
        allowEmployee: PropTypes.bool,
        mailValidationCode: PropTypes.bool,
        services: PropTypes.object.isRequired,
        handleNext: PropTypes.func.isRequired,
        handlePrev: PropTypes.func.isRequired,
        formData: PropTypes.object.isRequired,
        onSetFormData: PropTypes.func.isRequired,
    };

    static defaultProps = {
      allowNewCustomer: false,
      allowEmployee: true,
      mailValidationCode: false,
      isLoading: false,
    };

    /**
     * Short Country Code (2 letters) and Prefix (4 numbers) for phone number parsing
     *
     * @type {Array}
     */
    countryCodes = [{code: 'BE', prefix: '0032'},{code: 'FR', prefix: '0033'},{code: 'LU', prefix: '0352'}];

    state = {
        resetExistingUserAlertOpen: false,
        openUnknownUserDialog: false,
        countryCode: 'BE',
        confirmationDisabled: false
    };

    componentDidMount() {
      // Automatically focus phone input on load
      this.phoneInput.input.focus();
    }

    fetchExistingCustomer(phone) {
        if (!phone) {
            return null;
        }

        this.setState({ isLoading: true });

        // Fetch user from phone. If no existing user, return null
        API.getUserByPhone(phone)
            .then(response => {
                this.props.onSetFormData({
                    name: response.data.data.nom,
                    email: response.data.data.adresse_mail,
                    existingUser: response.data.data
                });

                this.setState({ isLoading: false });
            })
            .catch(error => {
                // If unknown phone and allowNewCustomer param is disabled,
                // show alert and reset phone
                if (404 === error.response.status && !this.props.allowNewCustomer) {
                  this.setState({
                    openUnknownUserDialog: true,
                  });
                }
                else {
                  console.log('Error fetching user: ', error.response);
                }

                this.setState({ isLoading: false });
            });
    }

    /**
     * Create custom label for input "Services"
     *
     * @returns {string}
     */
    getServicesLabel() {
        let contentServices = '';
        let i = 0;

        Object.keys(this.props.formData.checkedServices).forEach(serviceKey => {
            Object.keys(this.props.formData.checkedServices[serviceKey]).forEach(subServiceKey => {

                if (this.props.formData.checkedServices[serviceKey][subServiceKey]) {
                    if (i > 0) {
                        contentServices += ', ';
                    }

                    contentServices += this.props.services[serviceKey].services[subServiceKey].name;

                    i++;
                }
            });
        });

        return contentServices;
    }

    /**
     * Create custom label for input "Employee"
     *
     * @returns {string}
     */
    getEmployeeLabel() {
        let employee;

        if (this.props.formData.employeeId > 0) {
            Object.keys(this.props.employees).forEach(employeeId => {
                if (this.props.employees[employeeId].id === this.props.formData.employeeId) {
                    employee = this.props.employees[employeeId].prenom + ' ' + this.props.employees[employeeId].nom;
                }
            });
        } else {
            employee = 'Premier disponible';
        }
        
        return employee;
    }

    /**
     * Whether or not phone input is valid (for the country in this.countryCode)
     *
     * @param {String} phone
     * @returns {boolean}
     */
    isValidPhone(phone) {
        if (phone && phone.length > 5) {
            const parsedPhone = phoneUtil.parse(phone, this.state.countryCode);
            const isValid = phoneUtil.isValidNumber(parsedPhone);

            return isValid;
        }

        return false;
    }

    /**
     * Triggered when phone input change.
     * Parse the phone value.
     *
     * @param {String} phone
     */
    onChangePhone = (phone) => {
        // Noo need to format phone if less than 4 digits
        if (phone.length > 5) {
            // Format phone based on selected country
            let formattedPhone = phoneUtil.parse(phone, this.state.countryCode);
            phone = phoneUtil.format(formattedPhone, PhoneNumberFormat.NATIONAL);
        }

        this.props.onSetFormData({
            phone: phone,
            existingUser: null,
            email: this.props.formData.existingUser ? '' : this.props.formData.email,
            name: this.props.formData.existingUser ? '' : this.props.formData.name,
        });

        if (this.isValidPhone(phone)) {
            this.fetchExistingCustomer(phone);
        }
    };

    handleChangeCountry = (event, index, value) => {
      this.setState({ countryCode: value });

      this.onChangePhone(this.props.formData.phone);
    };

    resetExistingUser = () => {
        this.props.onSetFormData({
            existingUser: null,
            phone: '',
            email: '',
            name: '',
        });
    };

    /**
     * Returns phone status (required, invalid and valid) text description
     *
     * @param {String} phone
     * @returns {String}
     */
    getPhoneStatusMessage() {
        const phone = this.props.formData.phone;

        if (!phone || phone.length < 1)
            return 'Champ requis';
        else if (!this.isValidPhone(phone))
            return 'Numéro invalide';
        else
            return '';
    }

    /**
     * Whether or not email input is valid 
     *
     * @param {String} email
     * @returns {boolean}
     */
    isValidEmail(email) {
        let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        if (email && email.length > 5 && re.test(email)) {
            return true;
        }

        return false;
    }

    /**
     * Returns email status (required, invalid and valid) text description
     *
     * @param {String} email
     * @returns {String}
     */
    getEmailStatusMessage() {
        const email = this.props.formData.email;

        if ( ( (this.props.allowNewCustomer && !this.props.formData.existingUser) || (this.props.validationCode && this.props.mailValidationCode) ) && (!email || email.length < 1) )
            return 'Champ requis';
        else if ( ( (this.props.allowNewCustomer && !this.props.formData.existingUser) || (this.props.validationCode && this.props.mailValidationCode) ) && !this.isValidEmail(email) )
            return 'Adresse email invalide';
        else
            return '';
    }

    handleCloseExistingUserResetModal = () => {
        this.setState({ resetExistingUserAlertOpen: false });
    };

    handleCloseUnknownUserModal = () => {
      this.setState({
        openUnknownUserDialog: false,
      });

      this.props.onSetFormData({
        phone: '',
      });
    };

    handleNext = () => {
      this.setState({
        isLoading: true,
        confirmationDisabled: true
      });

      this.props.handleNext();
    };

    _getNameErrorText() {
        if (!this.props.formData.name)
            return 'Champ Requis';

        const nbWords = this.props.formData.name.trim().split(/\s+/).length - 1;

        if (nbWords <= 0) {
            return 'Veuillez entrez votre Prénom et votre Nom';
        }
        else {
            return '';
        }
    }

    renderNotMeLink() {
        if (!this.props.formData.existingUser) {
            return null;
        }

        const actions = [
            <FlatButton
                label="Annuler"
                primary={true}
                onClick={this.handleCloseExistingUserResetModal}
            />,
            <FlatButton
                label="Confirmer"
                primary={true}
                onClick={() => {
                    this.handleCloseExistingUserResetModal();
                    this.resetExistingUser();
                }}
            />,
        ];

        return (
            <div className="step-identity-notme-container">
                <FlatButton
                    onClick={() => { this.setState({ resetExistingUserAlertOpen: true })}}
                    labelStyle={{ fontSize: 10 }}
                    label="Ce n'est pas moi"
                    primary={true}
                />
                <Dialog
                    actions={actions}
                    modal={false}
                    open={this.state.resetExistingUserAlertOpen}
                    onRequestClose={this.handleCloseExistingUserResetModal}
                >
                    Ce numéro de téléphone est déjà utilisé.<br />
                    Voulez-vous en choisir un autre ?
                </Dialog>
            </div>
        );
    }

    renderLoading() {
      if (!this.state.isLoading)
        return null;

      return (
        <RefreshIndicator
          size={50}
          status="loading"
          style={{ top: '20%', left: '45%' }}
        />
      );
    }

    render() {
        // use moment.js to display date
        let date = moment(this.props.formData.date);

        return (
            <div>
                <div className="step-identity-container">
                    <div className="step-identity-phone-container">
                      <SelectField
                        autoWidth
                        value={this.state.countryCode}
                        onChange={this.handleChangeCountry}
                        style={{ width: 60 }}
                        underlineShow={false}
                      >
                        <MenuItem value={'BE'} key={'be'} primaryText={`BE`} />
                        <MenuItem value={'FR'} key={'fr'} primaryText={`FR`} />
                        <MenuItem value={'LU'} key={'lu'} primaryText={`LU`} />
                      </SelectField>
                      <TextField
                          ref={instance => this.phoneInput = instance}
                          type="tel"
                          value={this.props.formData.phone}
                          onChange={event => this.onChangePhone(event.target.value)}
                          hintText="0499 60 60 60"
                          floatingLabelText={`Téléphone (portable) ${this.isValidPhone(this.props.formData.phone) ? ' - Vérifié' : ''}`}
                          underlineShow={false}
                          floatingLabelStyle={this.isValidPhone(this.props.formData.phone)? { color: 'green' } : {}}
                          floatingLabelFocusStyle={this.isValidPhone(this.props.formData.phone)? { color: 'green' } : { color: 'red' }}
                          errorText={this.getPhoneStatusMessage()}
                          errorStyle={{ textAlign: 'left' }}
                      />
                    </div>
                  <br />
                    <Divider />
                    <TextField
                        type="email"
                        onChange={event => this.props.onSetFormData({ email: event.target.value })}
                        value={this.props.formData.email}
                        hintText="jean.dupont@gmail.com"
                        floatingLabelText={`Adresse mail ${this.isValidEmail(this.props.formData.email) ? ' - Vérifiée' : ''}`}
                        underlineShow={false}
                        floatingLabelStyle={this.isValidEmail(this.props.formData.email)? { color: 'green' } : {}}
                        floatingLabelFocusStyle={this.isValidEmail(this.props.formData.email)? { color: 'green' } : { color: 'red' }}
                        errorText={this.getEmailStatusMessage()}
                        errorStyle={{ textAlign: 'left' }}
                    />
                    <Divider />
                    <TextField
                        value={this.props.formData.name}
                        onChange={event => this.props.onSetFormData({ name: event.target.value })}
                        hintText="Jean Dupont"
                        floatingLabelText="Nom et Prénom"
                        underlineShow={false}
                        errorText={this._getNameErrorText()}
                        errorStyle={{ textAlign: 'left' }}
                        disabled={this.props.formData.existingUser? true : false}
                    />
                    {this.renderNotMeLink()}
                    <Divider />

                    {this.props.allowEmployee && (
                      <TextField
                        value={this.getEmployeeLabel()}
                        hintText="Collaborateur"
                        floatingLabelText="Collaborateur"
                        floatingLabelStyle={{ left: 0 }}
                        underlineShow={false}
                        multiLine={true}
                        inputStyle={{ color: 'black', fontSize: '0.8em' }}
                      />
                    )}

                    <Divider />
                    <TextField
                        value={this.getServicesLabel()}
                        hintText="Services"
                        floatingLabelText="Services"
                        floatingLabelStyle={{ left: 0 }}
                        underlineShow={false}
                        multiLine={true}
                        inputStyle={{ color: 'black', fontSize: '0.8em' }}
                    />
                    <Divider />
                    <TextField
                        value={this.props.formData.description}
                        onChange={event => this.props.onSetFormData({ description: event.target.value })}
                        floatingLabelText="Remarque"
                        floatingLabelStyle={{ left: 0 }}
                        underlineShow={false}
                        multiLine={true}
                        inputStyle={{ color: 'black', fontSize: '0.8em' }}
                    />
                    <Divider />

                    <Paper
                        className="step-identity-date-container"
                        zDepth={1}
                    >
                        <div className="step-identity-date-label">
                            {this.props.formData.date.getDate()}
                        </div>
                        {date.format('MMM').toUpperCase()}
                    </Paper>
                    <Chip
                        className="step-identity-chip"
                        backgroundColor={this.props.muiTheme.palette.accent1Color}
                        labelColor={this.props.muiTheme.palette.alternateTextColor}
                    >
                        <Avatar
                            color={this.props.muiTheme.palette.alternateTextColor}
                            icon={<DateIcon />}
                            backgroundColor={emphasize(this.props.muiTheme.palette.accent1Color)}
                        />
                        {(this.props.formData.time)? this.props.formData.time.label : ''}
                    </Chip>

                </div>

                <div className="step-identity-footer">
                    <FlatButton
                        label="Précédent"
                        onClick={this.props.handlePrev}
                        className="step-identity-btn-prev"
                    />
                    <RaisedButton
                        label='Confirmer'
                        primary={true}
                        disabled={this.state.confirmationDisabled || !(this.isValidPhone(this.props.formData.phone) && this.props.formData.name) || (this.props.validationCode && this.props.mailValidationCode && !this.isValidEmail(this.props.formData.email)) || (this.props.allowNewCustomer && !this.props.formData.existingUser && !this.isValidEmail(this.props.formData.email)) }
                        onClick={this.handleNext}
                    />
                </div>

                {this.renderLoading()}

                <UnknownCustomerDialog
                    open={this.state.openUnknownUserDialog}
                    onClose={this.handleCloseUnknownUserModal}
                />
            </div>
        )
    }
}

export default muiThemeable()(StepIdentity);


// WEBPACK FOOTER //
// ./src/StepIdentity.js